import type { MatchTypeFilter } from '../types/game';

const isDev = import.meta.env.DEV;

// ─── Dev Token Management ──────────────────────────────
// Allows switching between dev players at runtime.
// Seeded from VITE_DEV_TOKEN at module load so auth survives page refreshes.
let _devToken: string = import.meta.env.VITE_DEV_TOKEN || '';

export function setDevToken(token: string): void {
  _devToken = token;
}

export function getDevToken(): string {
  return _devToken;
}

// ─── Backend URL Resolution ────────────────────────────
// For mobile testing, set VITE_API_URL in your .env file
// e.g., VITE_API_URL=http://192.168.1.100:3000
//
// For web dev, defaults to localhost:3000
// For production, set to your real server URL
function getBaseUrl(): string {
  const envApiUrl = import.meta.env.VITE_API_URL?.trim();
  if (envApiUrl) return envApiUrl;

  // In dev, use relative URLs so Vite's proxy forwards /api and /cable to
  // localhost:3000 — this works whether the app is opened via localhost or
  // an ngrok tunnel, since the proxy runs on the server side.
  if (isDev) return '';

  console.error(
    '[apiConfig] Missing VITE_API_URL in production. ' +
    'API calls will use relative URLs and likely fail unless the frontend ' +
    'and backend share an origin.'
  );
  return '';
}

function getWsUrl(): string {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }
  const base = getBaseUrl();
  // Convert http(s) to ws(s)
  return base.replace(/^http/, 'ws') + '/cable';
}

export const API_CONFIG = {
  // REST API
  baseUrl: getBaseUrl(),

  // WebSocket (ActionCable)
  wsUrl: getWsUrl(),

  // API Endpoints
  endpoints: {
    // Sessions
    sessions: '/api/v1/sessions',
    session: (id: number) => `/api/v1/sessions/${id}`,
    joinSession: (id: number) => `/api/v1/sessions/${id}/join`,
    leaveSession: (id: number) => `/api/v1/sessions/${id}/leave`,
    rematchVote: (id: number) => `/api/v1/sessions/${id}/rematch_vote`,
    closeSoloSession: (id: number) => `/api/v1/sessions/${id}/close_solo`,
    sessionStatus: (id: number) => `/api/v1/sessions/${id}/status`,
    nextGame: (id: number) => `/api/v1/sessions/${id}/next_game`,
    roundReady: (id: number) => `/api/v1/sessions/${id}/round_ready`,
    sessionGames: (id: number) => `/api/v1/sessions/${id}/games`,

    // Lobby v1 — host-authoritative start + host/participant lobby controls
    startGame:       (id: number) => `/api/v1/sessions/${id}/start`,
    hostTheme:       (id: number) => `/api/v1/sessions/${id}/host_theme`,
    sessionVisibility: (id: number) => `/api/v1/sessions/${id}/visibility`,
    appearance:      (id: number) => `/api/v1/sessions/${id}/appearance`,
    ready:           (id: number) => `/api/v1/sessions/${id}/ready`,
    removeSeat:      (id: number, userId: number) => `/api/v1/sessions/${id}/remove_seat?user_id=${userId}`,
    moveSeat:        (id: number) => `/api/v1/sessions/${id}/move_seat`,

    // Games
    gameState: (id: number) => `/api/v1/games/${id}/state`,
    playTile: (id: number) => `/api/v1/games/${id}/play`,
    passTurn: (id: number) => `/api/v1/games/${id}/pass`,
    board: (id: number) => `/api/v1/games/${id}/board`,
    validPositions: (gameId: number, tileId: number) =>
      `/api/v1/games/${gameId}/board/valid_positions/${tileId}`,
    // Friend-spectating (read-only) — see Api::V1::Games::SpectateController.
    spectateGame: (id: number) => `/api/v1/games/${id}/spectate`,

    // Bot sessions
    botSession:  '/api/v1/sessions/bot',
    addBot:  (id: number) => `/api/v1/sessions/${id}/add_bot`,
    fillBots: (id: number) => `/api/v1/sessions/${id}/fill_bots`,

    // User
    me:                '/api/v1/me',
    updateUsername:    '/api/v1/me/username',
    updateCountry:     '/api/v1/me/country',
    updateLocale:      '/api/v1/me/locale',
    updateAvatar:      '/api/v1/me/avatar',
    updateTableSkin:   '/api/v1/me/table_skin',
    updateTileSkin:    '/api/v1/me/tile_skin',
    updateHdr:         '/api/v1/me/hdr',
    updatePush:        '/api/v1/me/push',
    pushSubscription:  '/api/v1/me/push_subscription',
    usernameAvailable: (u: string) => `/api/v1/users/username_available?u=${encodeURIComponent(u)}`,
    home:              '/api/v1/home',
    profile:           '/api/v1/profile',

    // Stats V1 — matchType is optional; omitted means the backend default (online_ranked).
    statsMe:       (matchType?: MatchTypeFilter) => `/api/v1/stats/me${matchType ? `?match_type=${matchType}` : ''}`,
    statsHistory:  (matchType?: MatchTypeFilter) => `/api/v1/stats/history${matchType ? `?match_type=${matchType}` : ''}`,
    statsPartners: (matchType?: MatchTypeFilter) => `/api/v1/stats/partners${matchType ? `?match_type=${matchType}` : ''}`,

    // Leaderboard V1 — ranked-only, page 1 only requested by the UI today (no pagination controls yet).
    leaderboard: (page: number, perPage: number) => `/api/v1/leaderboard?page=${page}&per_page=${perPage}`,

    achievements: '/api/v1/achievements',

    // Admin
    adminPresence: '/api/v1/admin/presence',

    // Friends
    friends:             '/api/v1/friends',
    friendsPending:      '/api/v1/friends/pending',
    friend:              (id: number) => `/api/v1/friends/${id}`,
    acceptFriend:        (id: number) => `/api/v1/friends/${id}/accept`,
    declineFriend:       (id: number) => `/api/v1/friends/${id}/decline`,
    // In-game/spectator "Add Friend" — targets a player by game context
    // (game_id + user_id) instead of email. See FriendsController#from_game.
    friendsFromGame:     '/api/v1/friends/from_game',

    // Users
    usersSearch: '/api/v1/users/search',

    // Game Invitations
    gameInvitations:        '/api/v1/game_invitations',
    gameInvitation:         (id: number) => `/api/v1/game_invitations/${id}`,
    acceptGameInvitation:   (id: number) => `/api/v1/game_invitations/${id}/accept`,
    declineGameInvitation:  (id: number) => `/api/v1/game_invitations/${id}/decline`,

    conversations:  '/api/v1/direct_messages/conversations',
    messageThread:  (userId: number) => `/api/v1/direct_messages/${userId}`,
    directMessages: '/api/v1/direct_messages',
  },

  // Firebase
  firebase: {
    projectId: 'the-domino-game',
  },
};
